<script lang="ts" setup>
import { Message, Skeleton, Button, IftaLabel, Panel } from 'primevue';
import { Form, FormField, type FormInstance } from '@primevue/forms';
import type { InnerCexCexOrdersModalProps } from '../CexCexOrdersModal.vue';
import SrInputNumber from '@/components/SrInputNumber.vue';
import { numberWithCommas, preventEnter, srZodNumber } from '@/utils';
import TriggerButton from '@/components/TriggerButton.vue';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from "zod"
import { i18n } from '@/i18n';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { isNull, isNumber } from 'lodash';
import { useCreateSwapOrderSettings } from '@/composable/settings/useCreateSwapOrderSettings';
import { useCexCexLimitsRefresher } from '@/composable/refreshers/useCexCexLimitsRefresher';
import { useProfiles } from '@/composable/useMultiProfiles';
import { useCexCexTwoLegsSettings } from '@/composable/settings/useCexCexTwoLegsSettings';
import ProfilesFieldset from '@/components/ProfilesFieldset.vue';
// import { useCheckUserRights } from '@/composable/useCheckUserRights';
import { useCexFeatures } from '@/composable/useCexFeatures';
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings';
import { useMyToast } from '@/composable/useMyToast';
import { useTriggerSettings } from '@/composable/settings/useTriggerSettings';
import { useAverageSpreadSettings } from '@/composable/settings/useAverageSpreadSettings';
import AverageSpreadButton from '@/components/dex-cex/AverageSpreadButton.vue';
import { useCexPositionsSide } from '@/composable/useCexPositionsSide';
import type { MarketType } from '@/utils/types';
import type { OrderSide } from '@/services/types';
import { useSound } from '@vueuse/sound'
import { useFlasherStore } from '@/stores/flasherStore'
import bellSoundFile from '@/assets/sounds/alert.wav'

const SLIPPAGE_MAX = 20
const SLIPPAGE_MIN = -20

const modeActive = ref(false)
const props = defineProps<InnerCexCexOrdersModalProps>()
const emit = defineEmits(['update-user-positions', 'show-positions-modal'])

// Триггер
const bellSound = useSound(bellSoundFile)
const flasherStore = useFlasherStore()

// const cacheKeyPostfix = computed(() => `cex/cex/${props.exBuy}/${props.exSell}/${props.asset}`.toLocaleLowerCase())
const cacheKeyPostfix = computed(() => props.cacheKey)
const { t } = i18n.global
const { avgSpread } = useAverageSpreadSettings(cacheKeyPostfix)
const advancedCexCollapsed = ref(true)
const approxOrderCost = ref<string>('')
const settingsForm = useTemplateRef<FormInstance>("settingsForm")
const disablePrepare = computed(() => autoPrepare.value ? isProfilesPrepared.value : false)
const isAnyProfileChoosen = computed(() => (enabledProfiles.value?.length ?? 0) > 0)
// Форма
const resolver = zodResolver(
  z.object({
    amount: srZodNumber()
  }),
)
const initValues = {
  amount: null,
  leverage: null,
  slippage: null,
}

// Композаблы
const { trailingSl, cleanTrailingSl, trailingActive, initTrailingStopLoss, trailingExit, trailingNext, trailingSide } = useTrailingSlSettings(cacheKeyPostfix)
const { toggleProfile, profiles, profilesMax, enabledProfiles } = useProfiles(cacheKeyPostfix, "cex_cex")
const { autoPrepare, orderNotifications } = useCexCexTwoLegsSettings()
const { updateLimits, limitsRefresher, limits } = useCexCexLimitsRefresher(props.exBuy, props.exSell, props.asset, props.marketType, props.sellAsset, props.quoteBuy, props.quoteSell)
const { isProfilesPrepared, preparingProfile, creatingOrder, fetchingBalance, fetchBalanceWrapper, prepareProfileWrapper, createOrderWrapper, createReduceOnlyOrderWrapper } = useCexFeatures(orderNotifications, enabledProfiles, profilesMax, "cex_cex")
const { initSettingsForm, leverage, amount, jumpRequired, slippage } = useCreateSwapOrderSettings(settingsForm, cacheKeyPostfix, "cex")
// const { isCexCexAdvanced } = useCheckUserRights()
const { addToast } = useMyToast()

const exBuy = computed(() => props.exBuy)
const asset = computed(() => props.asset)
const marketType = computed(() => "swap" as MarketType)
const { positionsSide } = useCexPositionsSide(exBuy, asset, marketType, enabledProfiles, props.quoteBuy)

const { triggerValue } = useTriggerSettings(cacheKeyPostfix, "cex")

watch(trailingSl, (v) => {
  localStorage.setItem('trailing-sl-cex', v ? '1' : '0')
})

const onAmountChange = (v: number | null | undefined) => {
  if (!props.priceBuy || !props.priceSell) {
    return
  }
  if (!v || !isNumber(v)) {
    approxOrderCost.value = ''
  } else {
    const average = (props.priceBuy + props.priceSell) / 2
    approxOrderCost.value = `(≈${Number((v * average) / (leverage.value ?? 1)).toFixed(2)} USDT)`
  }
}

const openPosition = async () => {
  const avg = avgSpread.value ?? 0
  const longSpread = Number(props.spread) - avg
  const shortSpread = Number(props.backSpread) + avg
  let isLong = false
  if (longSpread > 0.2) {
    isLong = true
  } else if (shortSpread > 0.2) {
    isLong = false
  } else {
    addToast({
      message: t('modal.createSwapOrders.noSpread'),
      severity: 'error',
    })
    return
  }
  await createOrderWargs(isLong ? 'buy' : 'sell')
  if (trailingSl.value) {
    if (props.latestExSellPrice) {
      initTrailingStopLoss(isLong, props.latestExSellPrice, avgSpread.value, props.spread, props.backSpread)
    }
  }
}

const closePosition = async () => {
  if (!props.exBuy || !props.asset || !props.priceBuy || !props.priceSell) {
    return
  }
  await createReduceOnlyOrderWrapper(props.exBuy, props.asset, props.priceBuy, props.priceSell, props.quoteBuy)
  cleanTrailingSl()
  updateClosedPositionsUntilSuccess()
}

const updateOpenPositionsUntilSuccess = () => {
  emit('update-user-positions')
  if (positionsSide.value === null) {
    window.setTimeout(() => {
      updateOpenPositionsUntilSuccess()
    }, 1500)
  }
}

const updateClosedPositionsUntilSuccess = () => {
  emit('update-user-positions')
  if (positionsSide.value !== null) {
    window.setTimeout(() => {
      updateOpenPositionsUntilSuccess()
    }, 1500)
  }
}

const createOrderWargs = async (side: "buy" | "sell") => {
  console.log(`createOrderWargs called`)
  addToast({
    message: t(
      side == 'buy'
        ? 'modal.createSwapOrders.placingBuyOrder'
        : 'modal.createSwapOrders.placingSellOrder',
    ),
    severity: 'success',
  })

  triggerValue.value = null
  if (autoPrepare.value && !isProfilesPrepared.value) {
    await prepareProfileWargs()
  }

  let orderPrice = side == "buy" ? props.priceBuy : props.priceSell
  if (!orderPrice) {
    throw Error("orderPrice is null")
  }

  const slippagePercent = (slippage.value ?? 0) / 100
  if (side == 'buy') {
    orderPrice += orderPrice * slippagePercent
  } else {
    orderPrice -= orderPrice * slippagePercent
  }

  let amountMaxByCost = limits?.value?.buy.cost_max ? Math.max(1, limits?.value?.buy.cost_max - 5) / orderPrice : null
  if (amountMaxByCost) {
    const ctrSize = props.contractSize
    if (ctrSize) {
      amountMaxByCost = Math.round(amountMaxByCost / ctrSize) * ctrSize
    }
  }
  const amountMax = limits?.value?.buy?.amount_max ?? null
  let amountMaxF = null
  if (amountMaxByCost && amountMax) {
    amountMaxF = Math.min(amountMaxByCost, amountMax)
  } else if (amountMaxByCost && !amountMax) {
    amountMaxF = amountMaxByCost
  } else if (!amountMaxByCost && amountMax) {
    amountMaxF = amountMax
  }

  let finalOrderAmount = amount.value
  if (amountMaxF && finalOrderAmount) {
    finalOrderAmount = Math.min(amountMaxF, finalOrderAmount)
  }

  if (!props.exBuy || !props.asset || !amount.value) {
    throw Error("exBuy or asset or amount is null")
  }

  await createOrderWrapper(
    props.exBuy,
    props.asset,
    side,
    amount.value,
    orderPrice,
    leverage.value ?? 1,
    false,
    props.quoteBuy,
  )
  updateOpenPositionsUntilSuccess()
}

const prepareProfileWargs = async () => {
  return await prepareProfileWrapper(props.exBuy as string, props.asset as string, leverage.value as number, props.quoteBuy)
}

const fetchBalanceWargs = async () => {
  return await fetchBalanceWrapper(props.exBuy as string)
}

// Следим за спредами для триггера
watch(() => props.spread, (v) => {
  if (v && modeActive) handleSpreadWatch(v, "buy")
})
watch(() => props.backSpread, (v) => {
  if (v && modeActive) handleSpreadWatch(v, "sell")
})

watch(
  [
    () => props.priceBuy,
    () => props.priceSell,
    () => props.latestExSellPrice,
    () => props.spread,
    () => props.backSpread,
  ],
  () => {
    moveTslIfRequired()
    checkIsTslBounded()
    checkIsSpreadClosed()
  },
)

// Логика работы трейлинг-стоп-лосса
const moveTslIfRequired = () => {
  if (!trailingSl.value || !trailingExit.value || !trailingNext.value || !props.latestExSellPrice || !props.backPriceSell || !props.priceBuy) {
    return
  }
  const isCexReachSl = (
    (positionsSide.value == "long" && props.backPriceSell >= trailingExit.value)
    ||
    (positionsSide.value == "short" && props.priceBuy <= trailingExit.value)
  )
  const isCex2ReachTp = (
    (positionsSide.value == "long" && props.latestExSellPrice >= trailingNext.value)
    ||
    (positionsSide.value == "short" && props.latestExSellPrice <= trailingNext.value)
  )
  if (trailingActive.value && isCex2ReachTp || !trailingActive.value && isCexReachSl) {
    const rightPrice = props.latestExSellPrice
    trailingActive.value = true
    const stepRatio = 1
    const priceRatio = rightPrice * stepRatio / 100
    if (positionsSide.value == "long") {
      trailingExit.value = rightPrice - priceRatio
      trailingNext.value = rightPrice + priceRatio
    } else {
      trailingExit.value = rightPrice + priceRatio
      trailingNext.value = rightPrice - priceRatio
    }
  }
}

const checkIsTslBounded = () => {
  if (!trailingSl.value || !trailingExit.value || !props.latestExSellPrice) {
    return
  }
  const isCex2ReachSl = (
    (positionsSide.value == "long" && props.latestExSellPrice <= trailingExit.value)
    ||
    (positionsSide.value == "short" && props.latestExSellPrice >= trailingExit.value)
  )
  if (isCex2ReachSl) {
    cleanTrailingSl()
    closePosition()
  }
}

const MIN_SPREAD_CLOSE = 0.5

const checkIsSpreadClosed = () => {
  if (!trailingSl.value || !trailingExit.value) {
    return
  }
  const isSpreadClosed = (
    (trailingSide.value == "long" && props.spread !== null && props.spread < MIN_SPREAD_CLOSE)
    ||
    (trailingSide.value == "short" && props.backSpread !== null && props.backSpread < MIN_SPREAD_CLOSE)
  )
  if (isSpreadClosed) {
    cleanTrailingSl()
    closePosition()
  }
}

const handleSpreadWatch = (
  v: number,
  side: "buy" | "sell",
) => {
  if (isNull(triggerValue.value) || isNull(v)) {
    return
  }

  if (v >= triggerValue.value) {
    if (jumpRequired.value) {
      const latestTicks = props.rightTicks?.slice(-5).map(x => x[1]) ?? []
      const isLong = side == "buy"
      const latestRightTickPrice = latestTicks?.[latestTicks.length - 1] ?? null
      if (latestRightTickPrice) {
        const minRightPrice = Math.min(...latestTicks)
        const maxRightPrice = Math.max(...latestTicks)
        const longSize = (latestRightTickPrice / minRightPrice - 1) * 100
        const shortSize = -(latestRightTickPrice / maxRightPrice - 1) * 100
        if (isLong && longSize < triggerValue.value * 0.8 || !isLong && shortSize < triggerValue.value * 0.8) {
          return
        }
      }
    }
    onSpreadTriggered(side)
  }
}

const onSpreadTriggered = async (side: OrderSide) => {
  console.log(`onSpreadTriggered called from twoLegs`)
  triggerValue.value = null
  bellSound.play()
  flasherStore.start()
  await createOrderWargs(side)
  if (trailingSl.value) {
    if (props.latestExSellPrice) {
      initTrailingStopLoss(side == "buy", props.latestExSellPrice, avgSpread.value, props.spread, props.backSpread)
    }
  }
}

const onAppears = async () => {
  modeActive.value = true
  console.log('one leg mode+')
  updateLimits()
  limitsRefresher.startRefresh()
  // Подгружаем из кэша данные формы
  initSettingsForm(props.recommendedAmount ?? null)
  onAmountChange(amount.value)
}

watch(leverage, () => {
  onAmountChange(amount.value)
})

const onDisappears = async () => {
  modeActive.value = false
  console.log('one leg mode-')
  limitsRefresher.stopRefresh()
}

defineExpose({
  onAppears,
  onDisappears
})
</script>

<template>
  <div>
    <div v-show="!minimized">
      <div class="grid grid-cols-2 px-1 py-2 border-color border-b-1">
        <div class="text-start">{{ $t('longSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.spread">{{ Number(props.spread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2">
        <div class="text-start">{{ $t('shortSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.backSpread">{{ Number(props.backSpread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <!-- Цена в лонг и цена в шорт -->
      <div class="grid grid-cols-2 px-1 py-2 border-color border-b-1 border-t-1">
        <div class="text-start">{{ $t('longPrice') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.priceBuy">{{ props.priceBuy }}</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2">
        <div class="text-start">{{ $t('shortPrice') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.priceSell">{{ props.priceSell }}</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <!-- Данные формы -->
      <Form ref="settingsForm" class="grid gap-1 w-full mt-4 grid-cols-1 md:grid-cols-3" :resolver="resolver"
        :initial-values="initValues" id="one-leg-form">
        <!-- Объём -->
        <FormField v-slot="$field" class="relative" name="amount">
          <IftaLabel>
            <SrInputNumber allow-empty @change="onAmountChange" @keydown="preventEnter" type="text"
              :maxFractionDigits="8" placeholder="1000" fluid />
            <label>{{ $t('modal.createSwapOrders.amount') }}{{ approxOrderCost }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>

        <!-- Плечо -->
        <FormField v-slot="$field" class="relative" name="leverage">
          <IftaLabel>
            <SrInputNumber showButtons :min="1" @keydown="preventEnter" :max="10" :step="1" :maxFractionDigits="0"
              placeholder="1" fluid />
            <label>{{ $t('modal.createSwapOrders.leverage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message, { min: 1, max: 10 }) }}
          </Message>
        </FormField>

        <!-- Проскальзывание -->
        <FormField v-slot="$field" class="relative" name="slippage">
          <IftaLabel>
            <SrInputNumber showButtons :step="0.1" @keydown="preventEnter" :maxFractionDigits="2" placeholder="3"
              fluid />
            <label>{{ $t('modal.createSwapOrders.slippage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message, { min: SLIPPAGE_MIN, max: SLIPPAGE_MAX }) }}
          </Message>
        </FormField>

        <!-- Чекбоксы -->
        <!-- <div class="col-span-full mt-4 flex justify-between items-center">
        <div class="flex gap-2 justify-end items-center">
          <Checkbox name="close" v-model="close" binary inputId="close" />
          <label for="close">{{ $t("modal.createSwapOrders.close") }}</label>
        </div>
      </div> -->
      </Form>
      <div class="grid grid-cols-2 px-1 py-2 py-2 border-b-1 border-t-1 border-color mt-6">
        <div class="flex justify-start items-center">
          <div class="text-start">{{ $t('modal.createSwapOrders.limitQuote') }}</div>
        </div>
        <div class="flex justify-end items-center">
          <div class="text-start">
            {{ limits?.buy?.amount_max ? numberWithCommas(limits?.buy?.amount_max) : '—' }}
          </div>
        </div>
      </div>
      <div class="grid grid-cols-2 px-1 py-2 py-2 border-color">
        <div class="flex justify-start items-center">
          <div class="text-start">{{ $t('modal.createSwapOrders.limitUsdt') }}</div>
        </div>
        <div class="flex justify-end items-center">
          <div class="text-start">
            {{ limits?.buy?.cost_max ? numberWithCommas(limits?.buy?.cost_max) : '—' }}
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-3 col-span-2">
          <div class="grid grid-cols-4 col-span-2">
            <Button @click="autoPrepare = !autoPrepare" class="rounded-r-none border-r-0" severity="success"
              variant="outlined" :icon="autoPrepare ? 'pi pi-check-square' : 'pi pi-stop'"
              :label="$t('modal.createSwapOrders.auto')">
              <!-- <i class="pi" :class="{ 'pi-check-square': autoPrepare, 'pi-stop': !autoPrepare }"></i> -->
              <!-- <span>{{ $t("modal.createSwapOrders.auto") }}</span> -->
            </Button>
            <Button severity="success" class="col-span-3 rounded-l-none" variant="outlined" icon="pi pi-user"
              :disabled="disablePrepare || preparingProfile || !isAnyProfileChoosen" @click="prepareProfileWargs"
              :loading="preparingProfile" :label="$t('modal.createSwapOrders.prepareProfile')">
            </Button>
          </div>

          <Button severity="success" class="col-span-2 lg:col-span-2" :disabled="!Boolean(priceSell) ||
            !leverage ||
            !amount ||
            creatingOrder ||
            enabledProfiles.length == 0 || preparingProfile
            " form="one-leg-form" type="button" variant="outlined" :loading="creatingOrder || preparingProfile"
            icon="pi pi-bolt" @click="openPosition" :label="$t('quickOpen')" fluid>
          </Button>

        </div>

        <Button severity="danger" class="col-span-2 lg:col-span-1" :disabled="!Boolean(priceSell) ||
          !leverage ||
          !amount ||
          creatingOrder ||
          enabledProfiles.length == 0 || preparingProfile
          " variant="outlined" :loading="creatingOrder || preparingProfile" icon="pi pi-bolt"
          :label="$t('modal.createSwapOrders.quickPosClose')" fluid @click="closePosition">
        </Button>

        <!-- Триггер -->
        <TriggerButton ref="triggerButton" class="col-span-2 lg:col-span-1" :root="cacheKeyPostfix" cache-key="cex"
          :amount="amount"></TriggerButton>

        <!-- Узнать баланс -->
        <Button fluid icon="pi pi-money-bill" severity="success" :loading="fetchingBalance"
          class="col-span-2 lg:col-span-1" :disabled="enabledProfiles.length == 0 || fetchingBalance"
          @click="fetchBalanceWargs" variant="outlined" :label="$t('modal.createSwapOrders.fetchBalance')">
        </Button>

        <!-- Уведомления -->
        <Button class="col-span-2 lg:col-span-1" :icon="orderNotifications ? 'pi pi-check-square' : 'pi pi-stop'"
          @click="orderNotifications = !orderNotifications" severity="success"
          :variant="orderNotifications ? undefined : 'outlined'" :label="$t('modal.createSwapOrders.notifications')">
        </Button>

        <!-- Мои позиции -->
        <Button fluid class="col-span-2 lg:col-span-2" severity="success" variant="outlined" icon="pi pi-shopping-cart"
          @click="emit('show-positions-modal')" :label="$t('modal.createSwapOrders.myPositions')">
        </Button>

      </div>
      <!-- <template v-if="isCexCexAdvanced"> -->
      <Panel toggleable :collapsed="advancedCexCollapsed" class="mt-4">
        <template #header>
          {{ $t('advancedCex') }}
        </template>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-2">

          <!-- Ср спред -->
          <AverageSpreadButton class="col-span-2" :root="cacheKeyPostfix" cache-key="dex-cex">
          </AverageSpreadButton>

          <!-- Стоп-лосс и тейк-профит -->
          <!-- <SlTpTriggerButton class="col-span-2 lg:col-span-1" :positionsSide="positionsSide"
          ref="closingTriggerButtonDex" severity="success" variant="outlined" :root="cacheKeyPostfix"
          :label="$t('modal.createSwapOrders.closingConditions')">
        </SlTpTriggerButton> -->

          <!-- Автозакрытие -->
          <!-- <AutoSlTpButton class="col-span-2 lg:col-span-1" ref="autoSltpButtonDex" :root="cacheKeyPostfix"
          severity="success" variant="outlined" :label="$t('modal.createSwapOrders.autoSlTp')">
        </AutoSlTpButton> -->

          <!-- Скользящий стоп-лосс -->
          <Button fluid class="col-span-2 lg:col-span-1" severity="success" variant="outlined"
            @click="trailingSl = !trailingSl" :icon="trailingSl ? 'pi pi-check-square' : 'pi pi-stop'"
            :label="$t('modal.createSwapOrders.trailingSl')">
          </Button>

          <!-- Резкие движения -->
          <Button :icon="jumpRequired ? 'pi pi-check-square' : 'pi pi-stop'" fluid class="col-span-2 lg:col-span-1"
            @click="jumpRequired = !jumpRequired" variant="outlined" severity="success" :label="$t('waitDexJump')" />

        </div>
      </Panel>
      <!-- </template> -->

      <ProfilesFieldset class="mb-15 md:mb-0" :toggle-profile="toggleProfile" :profiles="profiles"
        :enabled-profiles="enabledProfiles">
      </ProfilesFieldset>
    </div>
    <div v-show="minimized" class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <Button severity="danger" :disabled="!Boolean(priceSell) ||
        !leverage ||
        !amount ||
        creatingOrder ||
        enabledProfiles.length == 0 || preparingProfile
        " variant="outlined" :loading="creatingOrder || preparingProfile" icon="pi pi-bolt"
        :label="$t('modal.createSwapOrders.quickPosClose')" fluid @click="closePosition">
      </Button>
      <Button severity="success" :disabled="!Boolean(priceSell) ||
        !leverage ||
        !amount ||
        creatingOrder ||
        enabledProfiles.length == 0 || preparingProfile
        " form="one-leg-form" type="submit" variant="outlined" :loading="creatingOrder || preparingProfile"
        icon="pi pi-bolt" @click="openPosition" :label="$t('quickOpen')" fluid>
      </Button>
      <Button fluid severity="success" variant="outlined" @click="emit('show-positions-modal')"
        :label="$t('modal.createSwapOrders.myPositions')">
      </Button>
    </div>
  </div>
</template>
